import { Link } from 'expo-router';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { useChallengesUi } from '@fhs/loyalty/src/queries/loyalty.queries';
import { Favicon, HeaderText, MqSwitch, Pressable, Wordmark, XStack } from '@fhs/ui';
import { useClickLogs } from '@fhs-legacy/frontend/src/hooks/use-click-logs';
import { isExternalLink } from '@fhs-legacy/frontend/src/utils/is-external-link';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { openServiceModeDialog } from '../../../service-mode-dialog';
import { useHrefs } from '../../../use-hrefs';

const homeAnchorScreenReaderTextStyle = Platform.select({
  web: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden' as const,
    clip: 'rect(0, 0, 0, 0)' as const,
    whiteSpace: 'nowrap' as const,
    borderWidth: 0,
  },
  default: { display: 'none' as const },
});

export function HeaderLeftDesktop() {
  const { cateringHref, foundationHref, careersHref, franchiseHref } = useHrefs();
  const isCateringExternal = isExternalLink(cateringHref);
  const { data: challengesData } = useChallengesUi();

  const { logChallengeClickEvent, logCateringClickEvent, logFoundationClickEvent } = useClickLogs();

  return (
    <>
      <XStack style={styles.wordmarkContainer}>
        <Link
          href="/"
          dismissTo
          {...(Platform.OS !== 'web' ? { accessibilityLabel: 'Firehouse Subs Home' } : {})}
        >
          {Platform.OS === 'web' ? (
            <Text style={homeAnchorScreenReaderTextStyle}>Firehouse Subs Home</Text>
          ) : null}
          <View accessible={false} accessibilityElementsHidden>
            <MqSwitch
              $ltDesktopLg={() => <Favicon size={44} />}
              $gteDesktopLg={() => <Wordmark width={142} />}
            />
          </View>
        </Link>
      </XStack>

      <XStack style={styles.container}>
        <XStack style={styles.links}>
          <XStack style={styles.textWithCaret}>
            <Pressable aria-label="Order" onPress={openServiceModeDialog}>
              <HeaderText>Order</HeaderText>
            </Pressable>
          </XStack>

          {!!challengesData && challengesData.length > 0 && (
            <Link href={routes.offersV2} onPress={logChallengeClickEvent}>
              <HeaderText>Challenges</HeaderText>
            </Link>
          )}

          {Boolean(cateringHref) && (
            <Link
              href={cateringHref}
              onPress={logCateringClickEvent}
              {...(isCateringExternal
                ? { target: '_blank' as const, rel: 'noopener noreferrer' as const }
                : {})}
            >
              <HeaderText>Catering</HeaderText>
            </Link>
          )}

          <Link
            href={foundationHref}
            onPress={logFoundationClickEvent}
            target="_blank"
            rel="noopener noreferrer"
          >
            <HeaderText>Foundation</HeaderText>
          </Link>

          <Link href={careersHref} target="_blank" rel="noopener noreferrer">
            <HeaderText>Join Our Team</HeaderText>
          </Link>

          <Link href={franchiseHref} target="_blank" rel="noopener noreferrer">
            <HeaderText>Franchise</HeaderText>
          </Link>
        </XStack>
      </XStack>
    </>
  );
}

const styles = StyleSheet.create({
  wordmarkContainer: {
    alignItems: 'center',
  },
  container: {
    flexShrink: 1,
    gap: 24,
    alignItems: 'center',
  },
  links: {
    gap: 24,
    alignItems: 'center',
  },
  textWithCaret: {
    height: '100%',
    gap: 4,
    alignItems: 'center',
  },
  orderDropdownPane: {
    minWidth: 276,
    paddingTop: 32,
    paddingHorizontal: 16,
    paddingBottom: 16,
    alignItems: 'center',
  },
  orderDropdownGraphicAndButtonsStack: {
    gap: 24,
    width: '100%',
    alignItems: 'center',
  },
  orderDropdownButtonStack: {
    width: '100%',
    gap: 8,
  },
});
